import * as THREE from 'three';
import { Enemy, type DropTableEntry } from './Enemy.js';
import type { Game } from '../../game/Game.js';
import type { Circle } from '../../systems/Collision.js';
import { Projectile } from '../Projectile.js';

/**
 * Sentry drop table.
 *
 * From design doc:
 * - Weapon drops: common (70%)
 * - Head drops: rare (20%)
 * - No drop: 10%
 */
const SENTRY_DROPS: DropTableEntry[] = [
  { partId: 'repeater', weight: 15 },
  { partId: 'lancer', weight: 15 },
  { partId: 'sprayer', weight: 12 },
  { partId: 'seeker', weight: 8 },
  { partId: 'ricochet', weight: 8 },
  { partId: 'splitter', weight: 6 },
  { partId: 'mortar', weight: 5 },
  { partId: 'needler', weight: 5 },
  { partId: 'shocker', weight: 6 },
  { partId: 'scanner', weight: 5 },
  { partId: 'tracker', weight: 5 },
  { partId: 'spotter', weight: 4 },
  { partId: null, weight: 6 }, // No drop
];

/**
 * Sentry enemy - stationary turret.
 *
 * Behavior:
 * - Stationary (does not move)
 * - Detects player within radius
 * - Rotates to aim at player
 * - Fires projectiles at regular interval
 * - Predictable firing pattern (no random delays)
 *
 * Visual:
 * - Orange/red enemy color
 * - Box base (stationary)
 * - Cylinder turret on top that rotates
 * - Size: ~1 unit total
 *
 * Stats:
 * - HP: 50
 * - Detection radius: 12 units
 * - Fire rate: 1 shot per second
 * - Projectile speed: 8 units/sec
 * - Damage: 15 per shot
 */
export class Sentry extends Enemy {
  /** Time until next shot can be fired (seconds) */
  private fireTimer: number = 0;

  /** Shots per second */
  private readonly fireRate: number = 1;

  /** Projectile speed in units per second */
  private readonly projectileSpeed: number = 8;

  /** Damage dealt per projectile */
  private attackDamage: number = 15;

  /** How far the sentry can detect the player */
  private readonly detectionRadius: number = 12;

  /** Collision radius for hitbox */
  private readonly collisionRadius: number = 0.6;

  /** Reference to the rotating turret mesh (for aiming animation) */
  private turretMesh: THREE.Object3D | null = null;

  /** Telegraph duration before firing (seconds) */
  private readonly telegraphDuration: number = 0.3;

  /** Whether ready to fire after telegraph */
  private readyToFire: boolean = false;

  constructor(x: number, y: number, game: Game) {
    super(x, y, 50, SENTRY_DROPS, game);
    this.damage = this.attackDamage; // Set base damage for scaling
    this.createMesh();
  }

  /**
   * Create the sentry's visual mesh.
   *
   * Visual structure:
   * - Base (box) at ground level - doesn't rotate
   * - Turret (cylinder + barrel) on top - rotates to aim
   */
  private createMesh(): void {
    const group = new THREE.Group();

    // Base - stationary box
    const baseGeometry = new THREE.BoxGeometry(1, 0.4, 1);
    const baseMaterial = new THREE.MeshStandardMaterial({
      color: 0xff5722, // Orange-red enemy color
      metalness: 0.5,
      roughness: 0.6,
    });
    const base = new THREE.Mesh(baseGeometry, baseMaterial);
    base.position.y = 0.2; // Slightly above ground
    group.add(base);

    // Turret - rotating part
    this.turretMesh = new THREE.Group();

    // Turret body (cylinder)
    const turretGeometry = new THREE.CylinderGeometry(0.3, 0.3, 0.4, 16);
    const turretMaterial = new THREE.MeshStandardMaterial({
      color: 0xe64a19, // Darker orange-red
      metalness: 0.6,
      roughness: 0.5,
    });
    const turretBody = new THREE.Mesh(turretGeometry, turretMaterial);
    turretBody.position.y = 0.6; // On top of base
    this.turretMesh.add(turretBody);

    // Barrel (cylinder pointing forward)
    const barrelGeometry = new THREE.CylinderGeometry(0.1, 0.1, 0.6, 8);
    const barrelMaterial = new THREE.MeshStandardMaterial({
      color: 0x424242, // Dark gray
      metalness: 0.7,
      roughness: 0.4,
    });
    const barrel = new THREE.Mesh(barrelGeometry, barrelMaterial);
    barrel.rotation.z = Math.PI / 2; // Point horizontally
    barrel.position.set(0.35, 0.6, 0); // Offset from center, at turret height
    this.turretMesh.add(barrel);

    group.add(this.turretMesh);

    this.mesh = group;
    this.updateMeshTransform();
  }

  update(dt: number): void {
    const dtSec = dt / 1000;

    // Update telegraph animation
    const telegraphComplete = this.updateTelegraph(dtSec);
    if (telegraphComplete) {
      this.readyToFire = true;
    }

    // Decrement fire timer
    if (this.fireTimer > 0) {
      this.fireTimer -= dtSec;
    }

    // Find the player
    const player = this.findPlayer();
    if (!player) {
      return;
    }

    // Check if player is in detection range
    const dx = player.position.x - this.position.x;
    const dy = player.position.y - this.position.y;
    const distSq = dx * dx + dy * dy;

    if (distSq <= this.detectionRadius * this.detectionRadius) {
      // Player is in range - aim at them
      this.aimAtPlayer(player.position.x, player.position.y);

      // Start telegraph when ready to shoot
      if (this.fireTimer <= 0 && !this.isTelegraphing && !this.readyToFire) {
        this.startTelegraph(this.telegraphDuration);
      }

      // Fire when telegraph completes
      if (this.readyToFire) {
        this.fireAtPlayer(player.position.x, player.position.y);
        this.fireTimer = 1 / this.fireRate; // Reset cooldown
        this.readyToFire = false;
      }
    }

    this.updateMeshTransform();
  }

  /**
   * Find the player entity in the game.
   *
   * For now, we assume there's only one player mech.
   * Returns null if no player found.
   */
  private findPlayer(): { position: { x: number; y: number } } | null {
    // Access the game's player mech through a method we'll add to Game
    // For now, we'll need to add a getPlayer() method to Game
    // As a workaround, we'll search for a Mech entity
    return (this.game as any).playerMech || null;
  }

  /**
   * Rotate turret to aim at the player.
   *
   * @param targetX - Player X position
   * @param targetY - Player Y position
   */
  private aimAtPlayer(targetX: number, targetY: number): void {
    if (!this.turretMesh) return;

    const dx = targetX - this.position.x;
    const dy = targetY - this.position.y;

    // Calculate angle to target
    // atan2(dy, dx) gives angle from +X axis
    // We need to adjust for Three.js rotation around Y axis
    const angle = Math.atan2(dy, dx) - Math.PI / 2;

    // Rotate only the turret, not the base
    this.turretMesh.rotation.y = angle;
  }

  /**
   * Fire a projectile at the player.
   *
   * @param targetX - Player X position
   * @param targetY - Player Y position
   */
  private fireAtPlayer(targetX: number, targetY: number): void {
    const dx = targetX - this.position.x;
    const dy = targetY - this.position.y;
    const angle = Math.atan2(dy, dx);

    // Create projectile
    const projectile = new Projectile(
      this.position.x,
      this.position.y,
      angle,
      this.projectileSpeed,
      this.damage, // Use base damage (may be scaled)
      3 // Projectile lifetime in seconds
    );

    // Set owner to prevent self-collision
    projectile.setOwner(this);

    // Set room for wall collision (get from game)
    const room = this.game.getRoom();
    projectile.setRoom(room);

    // Change projectile color to red/orange for enemy projectiles
    if (projectile.mesh instanceof THREE.Mesh) {
      const material = projectile.mesh.material as THREE.MeshStandardMaterial;
      material.color.setHex(0xff5722); // Orange-red
      material.emissive.setHex(0xff5722);
    }

    // Set impact effect callback
    projectile.setOnDestroy((x, y) => {
      this.game.spawnImpactEffect(x, y, 0xff5722); // Orange impact for enemy projectiles
    });

    this.game.addEntity(projectile);
  }

  getCollisionShape(): Circle {
    return {
      x: this.position.x,
      y: this.position.y,
      radius: this.collisionRadius,
    };
  }
}
